import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'

import { EarningsCalendar } from './EarningsCalendar/EarningsCalendar'
import { EconomicCalendar } from './EconomicCalendar/EconomicCalendar'
import { MarketDataVendors } from './MarketDataVendors/MarketDataVendors'
import { PlatformVendorsPage } from './PlatformVendors/PlatformVendorsPage'
import { usePlatformRoutes } from './Platforms/PlatformsRoutes'
import { ResearchTerminal } from './ResearchTerminal/ResearchTerminal'

export const useToolsRoutes = (): RouteConfig[] => {
  const visible = useToolsRoutesConditions()
  const { isDefaultCFDProductType } = useProductReadContext()

  const platformRoutes = usePlatformRoutes()
  const { t } = useTranslation()

  return useMemo(
    (): RouteConfig[] => [
      {
        path: 'tools',
        condition: visible.isToolsVisible && isDefaultCFDProductType(),
        meta: { breadcrumbLabel: t('Tabs.Tools') },
        children: [
          ...platformRoutes,
          {
            path: 'earnings-calendar',
            element: <EarningsCalendar />,
            meta: { breadcrumbLabel: t('Earnings Calendar') },
          },
          {
            path: 'economic-calendar',
            element: <EconomicCalendar />,
            meta: { breadcrumbLabel: t('Economic Calendar') },
          },
          {
            path: 'research-terminal',
            element: <ResearchTerminal />,
            meta: {
              breadcrumbLabel: t('Research Terminal'),
            },
          },
        ],
      },
      {
        path: 'tools',
        condition: visible.isToolsVisible && !isDefaultCFDProductType(),
        meta: { breadcrumbLabel: t('Tabs.Tools') },
        children: [
          {
            path: 'platform-vendors',
            element: <PlatformVendorsPage />,
            meta: {
              breadcrumbLabel: t('Platform Vendors.title'),
            },
          },
          {
            path: 'market-data-vendors',
            element: <MarketDataVendors />,
            meta: {
              breadcrumbLabel: t('Market Data.Market Data Vendors'),
            },
          },
        ],
      },
    ],
    [isDefaultCFDProductType, platformRoutes, visible.isToolsVisible]
  )
}

interface RoutesConditions {
  isToolsVisible: boolean
}

export const useToolsRoutesConditions = (): RoutesConditions => {
  const { account } = useAccountReadContext()
  const { isDefaultCFDProductType } = useProductReadContext()

  const isToolsVisible = useMemo(
    () =>
      isDefaultCFDProductType()
        ? !!account?.visibilityConfiguration.hasTraderRoom
        : !!account?.visibilityConfiguration.hasEtdTraderRoom,
    [
      account?.visibilityConfiguration.hasEtdTraderRoom,
      account?.visibilityConfiguration.hasTraderRoom,
      isDefaultCFDProductType,
    ]
  )

  return {
    isToolsVisible,
  }
}
